import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/ui/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';

/**
 * Nuovo contatto: nome, che tipo e', un recapito. Basta.
 *
 * Il resto (P.IVA, sede, condizioni di pagamento, referenti) si aggiunge dopo
 * sulla scheda: chiedere tutto subito e' il modo piu' sicuro per non far
 * scrivere il contatto a nessuno, e allora la rubrica resta vuota.
 */
export function NuovoContattoPopover({ pending, onConfirm, onClose }: {
  pending?: boolean;
  onConfirm: (v: { nome: string; tipo: 'cliente' | 'potenziale' | 'professionista'; email: string | null; phone: string | null }) => void;
  onClose: () => void;
}) {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<'cliente' | 'potenziale' | 'professionista'>('cliente');
  const [recapito, setRecapito] = useState('');
  const mobile = useIsMobile();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Un campo solo per il recapito: si capisce dalla chiocciola se e' una mail
  // o un numero, e chi scrive non deve scegliere la casella giusta.
  const email = recapito.includes('@') ? recapito.trim() : null;
  const phone = recapito.trim() && !recapito.includes('@') ? recapito.trim() : null;

  const submit = () => {
    if (pending || nome.trim().length < 2) return;
    onConfirm({ nome: nome.trim(), tipo, email, phone });
  };

  // Sul telefono è un foglio dal basso come gli altri (mobile.css), portato in
  // fondo al documento: nato dentro la pagina restava nel suo livello, senza
  // velo sul resto e con la lista che si leggeva in trasparenza dietro i
  // campi (misurato il 6/10/2026). Sul computer resta agganciato al bottone.
  const corpo = (
    <>
      {/* ct-new-back / ct-new-pop: agganci per contacts.css. Sul computer non
          fanno niente; sotto i 768px il pannello si appoggia al bordo di sotto
          invece di restare agganciato a un bottone che li' non c'e' piu'. */}
      <div className="fixed inset-0 z-[60] ct-new-back" onClick={onClose} />
      <div
        className="dropdown adm-pop right-0 mt-1 z-[61] ct-new-pop"
        role="dialog"
        aria-label="Nuovo contatto"
        style={{ width: 'min(420px, calc(100vw - 32px))' }}
      >
        <div className="adm-pop-hd">
          <span className="t-micro">Nuovo contatto</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Nome</label>
          <input
            className="inp inp-sm"
            autoFocus
            placeholder="Ragione sociale o nome e cognome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          />
          <label className="field-label">Cos'è</label>
          <div className="seg-control">
            <button aria-pressed={tipo === 'cliente'} onClick={() => setTipo('cliente')}>Cliente</button>
            <button aria-pressed={tipo === 'potenziale'} onClick={() => setTipo('potenziale')}>Potenziale</button>
            <button aria-pressed={tipo === 'professionista'} onClick={() => setTipo('professionista')}>Professionista</button>
          </div>
          <label className="field-label">Recapito</label>
          <input
            className="inp inp-sm"
            placeholder="Email o telefono"
            value={recapito}
            onChange={(e) => setRecapito(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          />
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          {tipo === 'professionista'
            ? 'Finisce fra i professionisti: persone che lavorano per noi.'
            : tipo === 'potenziale'
              ? 'Scheda in anagrafica come potenziale: quando compra diventa cliente senza rifarla.'
              : 'Scheda cliente attiva: progetti, preventivi e fatture si agganciano qui.'}
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm btn-primary" disabled={pending || nome.trim().length < 2} onClick={submit}>
            <Icon name="plus" size={10} /> Crea
          </button>
        </div>
      </div>
    </>
  );
  return mobile ? createPortal(corpo, document.body) : corpo;
}
